import { initializeApp, getApps, getApp } from "firebase/app";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  signOut,
  sendEmailVerification,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  orderBy,
  doc,
  getDoc,
  limit,
  setDoc,
  startAfter,
  onSnapshot,
  serverTimestamp,
  where,
  updateDoc,
  increment,
} from "firebase/firestore";

class Fire {
  constructor() {
    this.init();
    this.lastVisible = null;
  }

  // Initialize Firebase
  init = () => {
    if (!getApps().length) {
      initializeApp({
        apiKey: process.env.REACT_APP_FIREBASE_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_SENDER,
        appId: process.env.REACT_APP_FIREBASE_APP,
        measurementId: process.env.REACT_APP_FIREBASE_MEASURE,
      });
    }
  };

  get analytics() {
    return getAnalytics;
  }

  /* ===========================
    Auth Api
  ============================*/
  get currentUser() {
    return getAuth().currentUser;
  }

  _currentUser = (callback) => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      callback(user);
    });
  };

  _updateUser = async (username, phone) => {
    await updateProfile(this.currentUser, { displayName: username, photoURL: phone });
  };

  _signUpWithEmailAndPassword = async (email, password, username, phone) => {
    const auth = getAuth();
    await setPersistence(auth, browserLocalPersistence);
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName: username, photoURL: phone });
    await sendEmailVerification(result.user);
  };

  _signInWithEmailAndPassword = async (email, password) => {
    const auth = getAuth();
    await setPersistence(auth, browserLocalPersistence);
    await signInWithEmailAndPassword(auth, email, password);
  };

  _resetPassword = async (email) => {
    const auth = getAuth();
    await sendPasswordResetEmail(auth, email);
  };

  _signOut = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      window.location.reload();
    } catch (e) {
      alert(e.message);
    }
  };

  _getUser = async (id) => {
    const db = getFirestore(getApp());
    const docRef = doc(db, "Users", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      alert("No Data Found");
      return null;
    }
  };

  _addUser = async (id, username, password, phone) => {
    try {
      const db = getFirestore(getApp());
      await setDoc(doc(db, "Users", id), { id, username, password, phone });
    } catch (error) {
      alert(error.message);
    }
  };

  /* ===========================
    Items Api
  ============================*/
  _getProducts = async () => {
    const db = getFirestore(getApp());
    const q = query(collection(db, "products"), orderBy("createdAt", "desc"), limit(20));
    const querySnapshot = await getDocs(q);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    this.lastVisible = lastVisible;
    const items = [];
    querySnapshot.forEach((doc) => {
      items.push(doc.data());
    });
    return items;
  };

  _getMoreProducts = async () => {
    const db = getFirestore(getApp());
    const q = query(collection(db, "products"), orderBy("createdAt", "desc"), startAfter(this.lastVisible), limit(20));
    const querySnapshot = await getDocs(q);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    this.lastVisible = lastVisible;
    const items = [];
    querySnapshot.forEach((doc) => {
      items.push(doc.data());
    });
    return items;
  };

  _getProduct = async (id) => {
    const db = getFirestore(getApp());
    const docRef = doc(db, "products", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      alert("No Data Found");
      return null;
    }
  };

  _updateProduct = async (id, data) => {
    const db = getFirestore(getApp());
    const docRef = doc(db, "products", id);
    await updateDoc(docRef, data);
  };

  _getBaskets = async () => {
    const db = getFirestore(getApp());
    const q = query(collection(db, "baskets"), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    const items = [];
    querySnapshot.forEach((doc) => {
      items.push(doc.data());
    });
    return items;
  };

  _getBasket = async (id) => {
    const db = getFirestore(getApp());
    const docRef = doc(db, "baskets", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return null;
    }
  };

  _updateBasket = async (id, data) => {
    const db = getFirestore(getApp());
    const docRef = doc(db, "baskets", id);
    await updateDoc(docRef, data);
  };

  /* ===========================
    Orders Api
  ============================*/
  _submitOrder = async (order) => {
    const db = getFirestore(getApp());
    const newRef = doc(collection(db, "orders"));
    order.id = newRef.id;
    order.createdAt = serverTimestamp();
    await setDoc(newRef, order);
  };

  _getOrders = async (id) => {
    const db = getFirestore(getApp());
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"), where("user.id", "==", id), limit(5));
    const querySnapshot = await getDocs(q);
    const orders = [];
    querySnapshot.forEach((doc) => {
      orders.push(doc.data());
    });

    return orders;
  };

  _getItem = async (id) => {
    const db = getFirestore(getApp());
    const docRef = doc(db, "orders", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return null;
    }
  };

  /* ===========================
    Ads Api
  ============================*/
  _getAds = async () => {
    const db = getFirestore(getApp());
    const q = query(collection(db, "ads"), orderBy("createdAt", "desc"), limit(2));

    const querySnapshot = await getDocs(q);
    const ads = [];
    querySnapshot.forEach((doc) => {
      ads.push(doc.data());
    });
    return ads;
  };

  /* ===========================
    Coupons Api
  ============================*/
  _getCoupon = async (code) => {
    const db = getFirestore(getApp());
    const docRef = doc(db, "coupons", code);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return null;
    }
  };

  /* ===========================
    Cart Api
  ============================*/
  _updateCart = async (data) => {
    try {
      if (!this.currentUser) return;
      const db = getFirestore(getApp());
      const docRef = doc(db, "carts", this.currentUser.uid);
      await setDoc(docRef, data);
    } catch (error) {
      alert(error.message);
    }
  };

  _getCart = (id, callback) => {
    const db = getFirestore(getApp());
    const docRef = doc(db, "carts", id);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data().items);
      } else {
        callback([]);
      }
    });
    return unsubscribe;
  };

  _updateItems = async (items) => {
    for (const item of items) {
      if (item.category) {
        this._updateProduct(item.id, { quantity: increment(-item.quantity) });
      } else {
        this._updateBasket(item.id, { quantity: increment(-item.quantity) });
      }
    }
  };

  /* ===========================
    Media Api
  ============================*/
  _uploadMedia = (path, uri, type, callback) => {
    const storage = getStorage();
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, uri, { contentType: type });

    // Listen for state changes, errors, and completion of the upload.
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log("Upload is " + progress + "% done");
        // eslint-disable-next-line default-case
        switch (snapshot.state) {
          case "paused":
            console.log("Upload is paused");
            break;
          case "running":
            console.log("Upload is running");
            break;
        }
      },
      (error) => {
        // A full list of error codes is available at
        // eslint-disable-next-line default-case
        switch (error.code) {
          case "storage/unauthorized":
            // User doesn't have permission to access the object
            break;
          case "storage/canceled":
            // User canceled the upload
            break;
          case "storage/unknown":
            // Unknown error occurred, inspect error.serverResponse
            break;
        }
      },
      () => {
        // Upload completed successfully, now we can get the download URL
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          callback(downloadURL);
        });
      }
    );
  };

  _deleteMedia = async (path) => {
    try {
      const storage = getStorage();
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (e) {
      alert(e.message);
    }
  };
}

export default new Fire();
